import {enableProdMode} from '@angular/core';
import {bootstrapApplication} from '@angular/platform-browser';
import {AppComponent} from './app/app.component';
import {appConfig} from './app/app.config';
import {environment} from './environments/environment';
import {captureApprovalToken, isIntegrationFlowPage} from './app/services/integrations/integration-approval-browser';

// First of all: an integration approval link carries its token in the URL fragment. It moves to sessionStorage and
// leaves the address bar before anything, Angular or analytics, reads the URL (engine connect-api.md, "Approval link").
captureApprovalToken();
// The pages of an integration's connection load no third-party script: no recording of a token or a provider code.
const thirdPartyScripts = !isIntegrationFlowPage(window.location.pathname);

if (environment.production) {
  enableProdMode();
}

// Intercom: install the queueing stub SYNCHRONOUSLY, before Angular bootstraps (above the call:
// bootstrapApplication runs the app initializers synchronously).
// IntercomService.intercom() is a no-op when window.Intercom is undefined, so if the stub only
// arrives in the deferred idle callback, an early boot()/show() (HomeComponent.ngAfterViewInit)
// is silently dropped and "Aide et support" opens nothing. Only the widget download is deferred.
// Same shape as the official snippet (`q` + `c`), which the widget replays on load.
type IntercomStub = ((...args: unknown[]) => void) & { q: unknown[][]; c: (args: unknown[]) => void };
const intercomStub = ((...args: unknown[]) => intercomStub.c(args)) as IntercomStub;
intercomStub.q = [];
intercomStub.c = (args) => { intercomStub.q.push(args); };
(window as unknown as { Intercom: IntercomStub }).Intercom = intercomStub;

// Bootstrap Angular immediately — no artificial delay
bootstrapApplication(AppComponent, appConfig)
	.catch(err => console.error(err));

// Defer third-party scripts until after first paint
function loadAfterPaint(fn: () => void): void {
	if ('requestIdleCallback' in window) {
		window.requestIdleCallback(fn);
	} else {
		setTimeout(fn, 3000);
	}
}

loadAfterPaint(() => {
	if (!thirdPartyScripts) {
		return;
	}

	// Hotjar (production only)
	if (environment.production) {
		// @ts-ignore
		(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:2819262,hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
	}

	// Intercom widget (the stub above replays the queued calls once it loads)
	const intercomScript = document.createElement('script');
	intercomScript.async = true;
	intercomScript.src = 'https://widget.intercom.io/widget/' + environment.intercomAppId;
	document.head.appendChild(intercomScript);

	// reCAPTCHA (only needed on signup page, but loaded lazily regardless)
	const head = document.getElementsByTagName('head')[0];
	const script = document.createElement('script');
	script.type = 'text/javascript';
	script.async = true;
	script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
	head.appendChild(script);
});
